import { call, select, put, takeEvery } from 'redux-saga/effects';
import { ReservationsResponse } from '@classpass/protobuf-definitions/lib/web/models/partners/reservation';
import { fetchEntity } from '@classpass/saga-tools';

import LINKS from 'constants/links';
import URLS from 'constants/urls';
import { openNewTab } from 'helpers/page-utils';
import { CLASS_ENTITY_TYPE, VENUE_SERVICE_TYPES, PERMISSION } from 'constants/logic';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { waitForModal } from 'components/Modal';
import { selectActiveVenue, selectUserHasPermission } from 'selectors/general';
import { venueClasses } from 'store/saga/entities/bound';
import messages from './ClassList.messages';

export const UPDATE_IS_SERVICE_ENABLED = 'UPDATE_IS_SERVICE_ENABLED';
export const updateIsServiceEnabled = (venueClass, isEnabled) => ({
  type: UPDATE_IS_SERVICE_ENABLED,
  venueClass,
  isEnabled,
});

export function* doUpdateIsServiceEnabled(dependencies, action) {
  try {
    const { httpClient } = dependencies;
    const { isEnabled, venueClass } = action;
    const { classId, classTitle, classPricingRule } = venueClass;
    const { venueId } = yield select(selectActiveVenue);
    const updateClassEndpoint = URLS.class.update(classId);

    if (!isEnabled) {
      const classReservationsResponse = yield call(
        [httpClient, httpClient.get],
        URLS.class.reservations(venueId, classId)
      );
      const { reservations } = ReservationsResponse.fromJSON(classReservationsResponse.data);

      const confirmed = yield call(waitForModal, dependencies, 'DisableServiceConfirmationModal', {
        classTitle,
        numberOfReservations: reservations?.length,
      });
      if (!confirmed) return;
    } else if (!classPricingRule) {
      const confirmed = yield call(waitForModal, dependencies, 'EnableServicePricingModal');
      if (confirmed) {
        yield call(openNewTab, LINKS.contact.partnerSuccessHref);
      }
      return;
    }

    yield call([httpClient, httpClient.patch], updateClassEndpoint, { disabled: !isEnabled });

    yield put(createInfoBanner(messages.updateActionSuccess));
    yield call(fetchEntity, venueClasses, [
      dependencies,
      {
        venueId,
        onlyActive: false,
        entityType: CLASS_ENTITY_TYPE.APPOINTMENT,
        includeClassPricing: true,
        includeDisabled: true,
      },
    ]);
  } catch (error) {
    yield put(createErrorBanner(messages.updateActionFailure));
  }
}

export function* handleClassesRouteEnter(client, action) {
  const activeVenue = yield select(selectActiveVenue);
  const venueId = action?.venueId || activeVenue?.venueId;
  const { autosyncStatus, serviceTypes } = activeVenue;
  const featureIsSupported = autosyncStatus && serviceTypes.includes(VENUE_SERVICE_TYPES.APPOINTMENT);
  const includeDisabledClasses = yield select(selectUserHasPermission, PERMISSION.FEATURE_DISABLE_INVENTORY);

  if (!featureIsSupported) {
    return;
  }

  yield call(fetchEntity, venueClasses, [
    client,
    {
      venueId,
      onlyActive: !includeDisabledClasses,
      entityType: CLASS_ENTITY_TYPE.APPOINTMENT,
      includeClassPricing: true,
      includeDisabled: includeDisabledClasses,
    },
  ]);
}

export function* watchClassListActions(client) {
  yield takeEvery(UPDATE_IS_SERVICE_ENABLED, doUpdateIsServiceEnabled, client);
}
